import { generateTabId } from "@/utils/sessionLogout";

// Cross-tab session LOGIN coordination — the mirror of `sessionLogout.ts`.
//
// When the user signs out in one tab, every sibling tab is sent to the sign-in
// page (see `useSessionLogoutListener`). Those tabs then sit on sign-in until
// the user touches them. Signing back in from any one tab publishes this
// message so the others can pick the fresh session up immediately: the
// session cookie is already shared browser-wide, so all a waiting tab has to
// do is reload — the server-side auth gate on the (auth) layout then sends it
// to the role's home page.
//
// BroadcastChannel only (same browser, same origin). There is deliberately no
// socket transport: a tab without a session has no socket to receive on, and
// other DEVICES must not react to a login here.

export const SESSION_LOGIN_CHANNEL = "sinek-session-login";

export interface SessionLoginMessage {
  type: "session_started";
  // Tab-unique id so the publishing tab can ignore its own echo.
  origin: string;
  // Wall-clock at which the message was produced (ISO string); for log
  // correlation only, never a business decision.
  emittedAt: string;
}

interface BuildSessionLoginMessageParams {
  origin: string;
  now: Date;
}

// Deterministic builder (Rule 7): `now` is injected by the caller.
export const buildSessionLoginMessage = (
  params: BuildSessionLoginMessageParams
): SessionLoginMessage => {
  return {
    type: "session_started",
    origin: params.origin,
    emittedAt: params.now.toISOString(),
  };
};

// Type guard (Rule 8): anything that is not exactly our shape is dropped
// upstream with a warning instead of being acted on.
export const isSessionLoginMessage = (
  raw: unknown
): raw is SessionLoginMessage => {
  if (!raw || typeof raw !== "object") return false;
  const m = raw as Record<string, unknown>;
  if (m.type !== "session_started") return false;
  if (typeof m.origin !== "string" || m.origin.length === 0) return false;
  return typeof m.emittedAt === "string" && m.emittedAt.length > 0;
};

// One-shot publisher used by every sign-in mutation right after the server
// confirmed the session. SSR / unsupported browser is a no-op: the failure
// mode is only "the other tab stays on sign-in until touched", which is the
// pre-existing behaviour.
export const publishSessionLogin = (params: { now: Date }): void => {
  if (typeof window === "undefined") return;
  if (typeof BroadcastChannel === "undefined") return;

  const channel = new BroadcastChannel(SESSION_LOGIN_CHANNEL);
  try {
    channel.postMessage(
      buildSessionLoginMessage({ origin: generateTabId(), now: params.now })
    );
  } finally {
    channel.close();
  }
};
